import { useTranslation } from 'react-i18next';
import type { IFieldDefinition } from '@platform/core/types';
import { TextCommit } from '@platform/features/workspaces/builder';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { autoWidthOf } from './form/formWidths';
import { groupEdits } from './form/groupEdits';
import { renamedGroupId } from './form/newGroupId';
import { FormFieldRow } from './FormFieldRow';
import type { IFormGroupEditorProps } from './IFormGroupEditorProps';

/**
 * Група в редакторі форми (прототип v4 feGroups): шапка — назва (зберігається, коли поле втрачає фокус чи натиснуто Enter), «N полів»,
 * «Увесь рядок», ↑ ↓ (порядок груп), × (вилучити групу — її поля стануть прихованими); рядки полів (FormFieldRow) або «Полів ще немає…».
 */
export function FormGroupEditor({ rt, group, index, total, name, groups, editor }: IFormGroupEditorProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const { form, apply, canEdit, published } = editor;
  const fields = group.keys.map((k) => rt.fields.find((f) => f.key === k)).filter((f): f is IFieldDefinition => !!f && !f.hidden);
  const off = !canEdit;
  return (
    <div className="st-feg">
      <div className="st-feg__head">
        <TextCommit className="st-input st-feg__name" value={name} label={t('modSettings.form.groupName')} disabled={off}
          onCommit={(v) => apply((f) => groupEdits.rename(f, group.id, v.trim(), renamedGroupId(group.id)))} />
        <span className="st-feg__count">{t('modSettings.form.fieldsN', { count: fields.length })}</span>
        <label className="st-feg__full">
          <input type="checkbox" className="st-check" checked={group.full} disabled={off} onChange={() => apply((f) => groupEdits.full(f, group.id))} />
          {t('modSettings.form.fullRow')}
        </label>
        <button type="button" className="st-btn st-btn--icon" disabled={off || index === 0} aria-label={t('modSettings.form.up')} onClick={() => apply((f) => groupEdits.moveGroup(f, group.id, -1))}>↑</button>
        <button type="button" className="st-btn st-btn--icon" disabled={off || index === total - 1} aria-label={t('modSettings.form.down')} onClick={() => apply((f) => groupEdits.moveGroup(f, group.id, 1))}>↓</button>
        <button type="button" className="st-btn st-btn--icon st-btn--danger" disabled={off} aria-label={t('modSettings.form.deleteGroupLabel')} title={t('modSettings.form.deleteGroup')}
          onClick={() => apply((f) => groupEdits.remove(f, group.id))}>×</button>
      </div>
      {fields.map((field, i) => {
        const label = strings.field(rt.key, field.key, field.label);
        return (
          <FormFieldRow key={field.key} field={field} label={label} width={form.widths[field.key]} auto={autoWidthOf(field, published)} groupId={group.id} groups={groups}
            first={i === 0} last={i === fields.length - 1} disabled={off}
            onWidth={(w) => apply((f) => groupEdits.width(f, field.key, w))}
            onMove={(target) => apply((f) => groupEdits.moveTo(f, field.key, target))}
            onStep={(step) => apply((f) => groupEdits.moveField(f, group.id, field.key, step))} />
        );
      })}
      {!fields.length && <div className="st-feg__empty">{t('modSettings.form.empty')}</div>}
    </div>
  );
}
